import React, { useState, useMemo, useEffect } from "react";
import { Box, Typography, Grid, Stack, Button, Container, useTheme, useMediaQuery } from "@mui/material";

import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import { useNavigate } from "react-router-dom";
import ROUTES from "router/routes.constants";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import { WealthChart } from "components/chart/lazy";

import CommonFeatureCard from "../../components/FinFactorInvestment/CommonFeatureCard";
import ComingSoonCard from "../../components/common/ComingSoonCard";
import AppCardComponent from "../../components/mui/AppCardComponent";
import FinFactorConsetDrawerContainer from "./FinFactorConsetDrawerContainer";
import FinFactorBankAccountsContainer from "./FinFactorBankAccountsContainer";
import NodisplayComponent from "../../components/common/NodisplayComponent";

import { parseDashboardOverview, wealthChartData } from "../../store/parsers/finfactor.parser";
import { useGetFinFactorDashboardQuery } from "../../store/api/finfactor.apislice";
import { useGetPortfolioSummaryQuery } from "../../store/api/portfolio.apislice";
// import { useRelativeTime } from "../../hooks/relativeTime";
import { getRelativeTime } from "../../utils/relativeTime";

import { useFinFactorJourneyRefresh } from "../../hooks/useFinFactorJourneyRefresh";
import { isNative } from "../../utils/platform.utils";
import FinancialGoalsIcon from "../../assets/FinFactor/FinancialGoals.svg";
import SmartInsightsIcon from "../../assets/FinFactor/SmartInsights.svg";
import FinancialGoalsImg from "../../assets/FinFactor/Financial Goals.png";
import SmartInsightsImg from "../../assets/FinFactor/Smart Insights & Recommendations.png";

const iconMap = {
  bank: <AccountBalanceOutlinedIcon />,
  investment: <WorkOutlineOutlinedIcon />,
  insurance: <ShieldOutlinedIcon />,
  liabilities: <GppGoodOutlinedIcon />,
};

// ── Prop shape for wealthData ────────────────────────────────────────────────
// wealthData: {
//   chartData:      array   – segments for the donut chart
//   centerLabel:    string  – e.g. "Total Wealth"
//   centerValue:    string  – e.g. "₹12,50,000"
//   centerSubLabel: string  – e.g. "Last Updated: 12 Jun 2025"
//   chartSize?:     number  – px size of donut (default 200)
//   showLegend?:    bool    – show/hide legend (default true)
//   legendGap?:     number  – gap between legend items (default 12)
// }
// ────────────────────────────────────────────────────────────────────────────

const FinFactorDashboardContainer = ({ wealthData }) => {
  const navigate = useNavigate();
  const [isConsentDrawerOpen, setIsConsentDrawerOpen] = useState(false);

  // Just came back from the consent journey? Re-pull every FinFactor API a few
  // times, since the AA data keeps arriving for a few seconds after approval.
  useFinFactorJourneyRefresh();

  const {
    data: apiOverview,
    isLoading,
  } = useGetFinFactorDashboardQuery();
  const { data: portfolioSummaryData } = useGetPortfolioSummaryQuery();

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Default fallback for overview if API is not loaded
  const overview = useMemo(() => {
    return parseDashboardOverview(apiOverview || {}, portfolioSummaryData?.result);
  }, [apiOverview, portfolioSummaryData]);

  // Handle mobile back gesture when drawer is open
  useEffect(() => {
    if (isConsentDrawerOpen) {
      const handlePopState = () => {
        setIsConsentDrawerOpen(false);
      };

      // Add a dummy entry to history so we can catch the back button
      // Use setTimeout to avoid conflict with shims/React mount phase
      const timer = setTimeout(() => {
        try {
          if (window.history.state?.drawer !== "consent") {
            window.history.pushState({ drawer: "consent" }, "");
          }
        } catch (error) {
          console.error("Failed to push history state:", error);
        }
      }, 0);

      window.addEventListener("popstate", handlePopState);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("popstate", handlePopState);
      };
    }
  }, [isConsentDrawerOpen]);

  const handleDrawerClose = (options = {}) => {
    const { skipHistoryBack = false } = options;
    // If we're closing manually, pop the state we pushed
    if (!skipHistoryBack && window.history.state?.drawer === "consent") {
      window.history.back();
    }
    setIsConsentDrawerOpen(false);
  };

  const {
    financialGoals = { title: "Financial Goals", comingSoon: true },
    smartInsights = { title: "Smart Insights & Recommendations", comingSoon: true },
  } = overview || {};
  const accounts = overview?.accounts || [];

  // ← Yahan hook call karo with raw timestamp
  const relativeLastUpdated = getRelativeTime(overview?.wealth?.lastUpdated);

  // Destructure wealth props — fall back to mock data if nothing is passed
  const {
    chartData = overview?.chartData || wealthChartData,
    centerLabel = overview?.wealth?.totalWealthLabel,
    centerValue = overview?.wealth?.formattedWealth,
    centerSubLabel = relativeLastUpdated
      ? (isXs ? (
        <>
          Last Updated:<br />{relativeLastUpdated}
        </>
      ) : `Last Updated: ${relativeLastUpdated}`)
      : "",
    chartSize = isXs ? 400 : (isMobile ? 520 : 360),
    showLegend = true,
    legendGap = 16,
  } = wealthData ?? {};

  if (isLoading) {
    return null; // Or a skeleton/loader
  }

  const handleCardClick = (item) => {
    if (item.status === "comingSoon") return;
    if (item.title === "Banking") {
      navigate(ROUTES.FINFACTOR_BANKING);
    } else if (item.title === "Insurance") {
      navigate(ROUTES.FINFACTOR_INSURANCE);
    } else if (item.route) {
      navigate(item.route);
    }
  };

  return (
    <Container
      maxWidth="lg"
      sx={{ p: { xs: 0.5, sm: 2, md: 0 }, py: { xs: 2, md: 4 } }}
      disableGutters
    >
      <Stack spacing={{ xs: 2, md: 3 }}>
        {/* Overview Card */}
        <AppCardComponent
          sx={{
            backgroundColor: "#13172a",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.05)",
            color: "white",
            p: { xs: 1.5, md: 2.5 },
            // pt: 4,
            "& .MuiCardContent-root": {
              p: 0,
              "&:last-child": { pb: 0 },
            },
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
            mb={{ xs: 1, md: 0 }}
          >
            <Typography
              sx={{ fontSize: { xs: "20px", md: "24px" }, fontWeight: 400 }}
            >
              Financial Overview
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setIsConsentDrawerOpen(true)}
              sx={{
                width: { xs: "100%", sm: "auto" },
                color: "white",
                borderColor: "rgba(255,255,255,1)",
                borderRadius: "18px",
                textTransform: "none",
                fontWeight: 400,
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.05)",
                },
              }}
            >
              Manage Consent
            </Button>
          </Stack>

          <Grid
            container
            spacing={{ xs: 2.5, md: 2.5 }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "visible",
                  cursor: "pointer",
                }}
              >
                {Array.isArray(chartData) && chartData.some((item) => (item.value || 0) > 0) ? (<WealthChart
                  data={chartData}
                  centerLabel={centerLabel}
                  centerValue={centerValue}
                  centerSubLabel={centerSubLabel}
                  chartSize={chartSize}
                  centerValueFontSize={isNative() ? (isXs ? 24 : 28) : (isXs ? 24 : 26)}
                  centerValueFontWeight={600}
                  centerLabelFontSize={isNative() ? (isXs ? 14 : 16) : (isXs ? 13 : 14)}
                  centerSubLabelFontSize={isNative() ? (isXs ? 12 : 14) : (isXs ? 11 : 12)}
                  legendFontSize={isXs ? 13 : 12}
                  labelFontSize={isMobile ? (isXs ? 9 : 11) : 10}
                  toggleLabelsOnMobile={true}
                  {...(showLegend !== undefined && { showLegend })}
                  {...(legendGap !== undefined && { legendGap })}
                />
                ) : (
                  <NodisplayComponent
                    title="No Data Available"
                    subtitle="We couldn't find any financial overview data at the moment."
                    containerSx={{
                      minHeight: { xs: "250px", md: "360px" },
                      padding: 0
                    }}
                    imageHeight={{ xs: "100px", md: "140px" }}
                  />
                )}
              </Box>
            </Grid>

            {/* Accounts Grid */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                {accounts.map((item) => (
                  <Grid item xs={12} sm={6} key={item.id}>
                    <Box
                      onClick={() => handleCardClick(item)}
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.03)",
                        borderRadius: "12px",
                        border: "1px solid rgba(255,255,255,0.08)",
                        p: { xs: 2.5, md: 3 },
                        height: "100%",
                        cursor:
                          item.status === "comingSoon" ? "default" : "pointer",
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.06)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        },
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Stack
                          direction="row"
                          spacing={{ xs: 3, md: 1.5 }}
                          alignItems="center"
                          mb={1.5}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: "#13172A",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "rgba(255, 255, 255, 0.7)",
                              border: "1px solid rgba(255, 255, 255, 0.08)",
                            }}
                          >
                            {React.cloneElement(iconMap[item.icon], {
                              sx: { fontSize: "18px" },
                            })}
                          </Box>
                          <Typography
                            color="rgba(255,255,255,0.6)"
                            fontSize={{ xs: "12px", md: "13px" }}
                            fontWeight={400}
                          >
                            {item.title}
                          </Typography>
                        </Stack>

                        <Box
                          sx={{
                            filter:
                              item.status === "comingSoon"
                                ? "blur(4px)"
                                : "none",
                            opacity: item.status === "comingSoon" ? 0.6 : 1,
                            transition: "all 0.3s",
                          }}
                        >
                          <Typography
                            fontSize={{ xs: "20px", md: "22px" }}
                            fontWeight={600}
                            color="white"
                            mb={0.5}
                          >
                            {item.value || "-"}
                          </Typography>
                          <Typography
                            fontSize={{ xs: "12px", md: "13px" }}
                            color="rgba(255,255,255,0.5)"
                          >
                            {item.subtitle}
                          </Typography>
                        </Box>
                      </Box>

                      {!item.status && (
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            border: "1px solid rgba(56, 189, 248, 0.4)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#38bdf8",
                            backgroundColor: "rgba(56, 189, 248, 0.05)",
                          }}
                        >
                          <ChevronRightOutlinedIcon sx={{ fontSize: "18px" }} />
                        </Box>
                      )}

                      {item.status === "comingSoon" && (
                        <Box
                          position="absolute"
                          left="50%"
                          top="50%"
                          sx={{ transform: "translate(-50%, -50%)", zIndex: 2 }}
                        >
                          <ComingSoonCard />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </AppCardComponent>

        {/* Financial Goals */}
        <AppCardComponent
          sx={{
            backgroundColor: "#13172a",
            borderRadius: "12px",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "white",
            p: { xs: 2.5, md: 3 },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            mb={3}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <img src={FinancialGoalsIcon} width={24} />
              <Typography fontWeight={600}>{financialGoals.title}</Typography>
            </Stack>
            {/* <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
                            sx={{
                                backgroundColor: "rgba(255,255,255,0.15)",
                                textTransform: "none",
                                fontSize: "12px",
                                fontWeight: 600,
                                px: 1.5, py: 0.25,
                                minWidth: "auto",
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" }
                            }}
                        >
                            {financialGoals.button.text}
                        </Button> */}
          </Stack>

          <Box position="relative">
            <img src={FinancialGoalsImg} width="100%" />
            {financialGoals.comingSoon && (
              <Box
                position="absolute"
                top="50%"
                left="50%"
                sx={{ transform: "translate(-50%,-50%)" }}
              >
                <ComingSoonCard />
              </Box>
            )}
          </Box>
        </AppCardComponent>

        {/* Smart Insights */}
        <AppCardComponent
          sx={{
            backgroundColor: "#13172a",
            borderRadius: "12px",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "white",
            p: { xs: 2.5, md: 3 },
          }}
        >
          <Stack direction="row" spacing={1} mb={3}>
            <img src={SmartInsightsIcon} width={24} />
            <Typography fontWeight={600}>{smartInsights.title}</Typography>
          </Stack>

          <Box position="relative">
            <img src={SmartInsightsImg} width="100%" />
            {smartInsights.comingSoon && (
              <Box
                position="absolute"
                top="50%"
                left="50%"
                sx={{ transform: "translate(-50%,-50%)" }}
              >
                <ComingSoonCard />
              </Box>
            )}
          </Box>
        </AppCardComponent>

        <FinFactorConsetDrawerContainer
          open={isConsentDrawerOpen}
          onClose={handleDrawerClose}
        />
      </Stack>
    </Container>
  );
};

export default FinFactorDashboardContainer;
